import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  XCircle,
  CheckCircle,
  FileText,
  Mail,
  Building2,
  Scale,
  Leaf,
  Globe,
  FileCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';

// What the portal is NOT
const NOT_ALLOWED = [
  'It does not allow vendors to influence rankings or matching',
  'It does not provide sponsored placement',
  'It does not change independent assessments',
  'It is not a marketing or advertising platform',
];

// How access works steps
const ACCESS_STEPS = [
  {
    number: '1',
    title: 'Your platform may already be listed',
    description: 'Many AI platforms are already included in GoCompareAI in an unclaimed state.',
  },
  {
    number: '2',
    title: 'Apply to claim your profile',
    description: 'Vendors can apply to claim or manage their platform profile.',
  },
  {
    number: '3',
    title: 'Access granted after review',
    description: 'Applications are reviewed to ensure legitimacy and agreement to terms.',
  },
  {
    number: '4',
    title: 'Receive secure invitation',
    description: 'Approved vendors receive an email invitation to create login credentials.',
  },
];

export default function VendorInfoPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO pageType="vendors" />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="w-full py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 mb-8">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6 leading-tight tracking-tight">
              A neutral way to represent your AI platform clearly
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              GoCompareAI provides AI vendors with a structured, transparent way to ensure 
              their platform is accurately represented — without influencing rankings or recommendations.
            </p>
          </div>
        </section>

        {/* What the Vendor Portal Is */}
        <section className="w-full py-12 sm:py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  What the Vendor Portal is
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  The GoCompareAI Vendor Portal allows approved AI providers to manage their 
                  factual platform information, add official documentation, and keep details 
                  up to date — all within a neutral comparison framework.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What the Vendor Portal Is NOT */}
        <section className="w-full py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  What the Vendor Portal is not
                </h2>
                <ul className="space-y-3 mb-6">
                  {NOT_ALLOWED.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground italic">
                  Independence and transparency are core to how GoCompareAI works.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How Access Works */}
        <section className="w-full py-12 sm:py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-semibold text-foreground mb-8 text-center">
              How access works
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {ACCESS_STEPS.map((step, index) => (
                <Card key={index} className="border-border/50 bg-background/80">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-semibold text-primary">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground mb-1">{step.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Sustainability Module Section */}
        <section className="w-full py-16 sm:py-20 bg-green-50/50 dark:bg-green-950/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <Badge variant="outline" className="mb-4 border-green-500/30 text-green-700 dark:text-green-400">
                New Feature
              </Badge>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-500/10 mb-6">
                <Leaf className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-4">
                Sustainability & Environmental Disclosures
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Vendors can now share environmental information through GoCompareAI, 
                helping procurement teams and users make informed decisions.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <Card className="border-green-200/50">
                <CardContent className="p-6">
                  <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-green-600" />
                    What you can disclose
                  </h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Infrastructure providers and data centre regions
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Sustainability commitments and environmental goals
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Carbon reporting availability for customers
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Offset and mitigation measures
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-green-200/50">
                <CardContent className="p-6">
                  <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-green-600" />
                    Evidence & certifications
                  </h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      ISO 14001 and environmental certifications
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Carbon Trust or SBTi commitments
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      ESG and sustainability reports
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Third-party audit documentation
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-white/50 dark:bg-background/50 border-green-200/50">
              <CardContent className="p-6">
                <h3 className="font-medium text-foreground mb-3">Module availability</h3>
                <div className="grid sm:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="font-medium text-foreground">Enterprise Tier</p>
                    <p className="text-muted-foreground text-xs mt-1">Included by default with auto-publish for safe fields</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="font-medium text-foreground">Verified Tier</p>
                    <p className="text-muted-foreground text-xs mt-1">Available as add-on, subject to admin review</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="font-medium text-foreground">Claimed Tier</p>
                    <p className="text-muted-foreground text-xs mt-1">Available as add-on with view-only access initially</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  All sustainability information is clearly labelled as vendor-provided. 
                  GoCompareAI does not calculate, score, or rank environmental impact.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-6">
              Ready to claim your platform?
            </h2>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Button 
                size="lg" 
                asChild
                className="h-12 px-8 text-base gap-2 group"
                data-testid="apply-claim-btn"
              >
                <Link to="/suggest?type=vendor">
                  Apply to claim your platform
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
            </div>
            
            <Link 
              to="/vendor" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Already invited? Sign in to the Vendor Portal
            </Link>
          </div>
        </section>

        {/* Trust Statement */}
        <section className="w-full py-12 sm:py-16 bg-muted/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Card className="border-border/50 bg-background/80">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    GoCompareAI does not sell preferential placement, rankings, or recommendations. 
                    Vendor participation is limited to factual accuracy and transparency.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
